/** The continuous-voice conversation loop, shared by the exam speaking
 * partner and the standalone speaking bot.
 *
 * Start ONCE → the partner greets aloud → the microphone arms itself →
 * each finished turn goes to the server → the reply is spoken → the mic
 * re-arms. No per-turn button; it runs until the learner ends it (or the
 * server says ended).
 *
 * WHO DOES THE LISTENING changed, and it is the reason this file exists in
 * its present shape. The browser's own recogniser used to hear the learner,
 * and it was the cause of both halves of the client's complaint: it ended a
 * turn after about a second of silence ("stopping in between"), and its
 * German was whatever the phone had ("not listening clearly"). Now the
 * audio is recorded and the SERVER transcribes it, with one good model for
 * everyone — see utils/recorder.ts for how a turn is ended by real silence,
 * or by the learner tapping Done.
 *
 * The browser recogniser is kept as the fallback for a deployment whose
 * server cannot listen (no transcription key), so nothing goes dark there.
 * Text turns are what the server sees either way.
 */
import { useEffect, useRef, useState } from "react";
import * as api from "../../api/exams";
import { ApiError } from "../../api/http";
import { transcribe, voiceStatus } from "../../api/speech";
import {
  createRecorder,
  filenameFor,
  recorderAvailable,
  silenceWindowFor,
  type Recorder,
} from "../../utils/recorder";
import { createRecognizer, sttAvailable, type Recognizer } from "../../utils/stt";
import { cancelVoice, speakBest, unlockVoice } from "../../utils/voice";

export interface VoiceConversation {
  session: { id: string; maxTurns: number | null } | null;
  messages: api.SpeakingTurn[];
  turns: number;
  busy: boolean;
  listening: boolean;
  /** The partner's voice is actually playing. Distinct from "not
   *  listening": the orb used to treat speaking as its FALLBACK state, so
   *  a conversation that had quietly stalled looked exactly like one where
   *  the partner was talking — which is how the client read it. */
  speaking: boolean;
  /** The learner's turn has been recorded and is being written down by the
   *  server. Shown, because the gap would otherwise read as a dead mic. */
  transcribing: boolean;
  /** The last recording came back empty — ask them to say it again. */
  missed: boolean;
  /** Microphone level 0..1 while listening, so the learner can SEE that
   *  they are being heard. Always 0 on the browser-recogniser fallback. */
  level: number;
  /** True when the learner can end their turn by hand (server listening).
   *  The browser recogniser decides its own turn ends. */
  canFinish: boolean;
  error: string | null;
  ended: api.SpeakingSession | null;
  start: () => Promise<void>;
  sendText: (text: string) => void;
  /** "I have finished my sentence" — deliver the recording now rather than
   *  waiting for the silence timer. */
  finishTurn: () => void;
  end: () => Promise<void>;
  reset: () => void;
  /** Re-enter a conversation the server still has open. */
  resume: (session: api.SpeakingSession) => void;
  /** What the microphone is picking up right now (browser recogniser), so
   *  a learner can see they are being heard. Empty between turns. */
  heard: string;
  /** True once a resumed conversation is showing but the mic is not armed
   *  yet — the learner has to say "continue", which is also the gesture
   *  browsers want before re-opening the microphone. */
  paused: boolean;
  /** Arm the mic on a resumed conversation. */
  listen: () => void;
  showEnded: (session: api.SpeakingSession) => void;
}

export function useVoiceConversation(opts: {
  /** Opens the session server-side (exam item, or a free chat). */
  startSession: () => Promise<api.SpeakingStartResult>;
  /** The model this conversation is being held with, resent on every turn
   *  so the partner - and the price - cannot change halfway through.
   *  Null = the platform default. */
  model?: string | null;
  /** Speech locale, e.g. "de-DE". */
  lang?: string;
  /** The learner's CEFR level, which sets how long a silence has to be
   *  before their turn is taken as finished. A beginner needs seconds to
   *  find a word; see silenceWindowFor(). */
  level?: string | null;
  /** Fired when a conversation finishes (e.g. to refresh history). */
  onFinished?: () => void;
}): VoiceConversation {
  const { startSession, lang, level: cefrLevel = null, onFinished, model = null } = opts;
  const [session, setSession] = useState<{ id: string; maxTurns: number | null } | null>(null);
  const [messages, setMessages] = useState<api.SpeakingTurn[]>([]);
  const [turns, setTurns] = useState(0);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [missed, setMissed] = useState(false);
  const [level, setLevel] = useState(0);
  const [canFinish, setCanFinish] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState<api.SpeakingSession | null>(null);
  const [paused, setPaused] = useState(false);
  /** What the recogniser thinks it is hearing right now. */
  const [heard, setHeard] = useState("");

  const recognizerRef = useRef<Recognizer | null>(null);
  const recorderRef = useRef<Recorder | null>(null);
  /** Server listening is in use for this conversation. */
  const serverRef = useRef(false);
  const liveRef = useRef(false);
  const busyRef = useRef(false);
  /** A clip is being written down. Separate from busyRef, which only
   *  covers the reply: between the recording ending and the transcript
   *  coming back, a second clip could otherwise slip in - two turns for
   *  one sentence, and two bills. A test found exactly that. */
  const clipRef = useRef(false);
  const sessionRef = useRef<string | null>(null);
  // In a ref, not read from the closure: sendText is held by the
  // recogniser's callbacks for the whole conversation, so a prop read
  // there would be whatever it was when the chat started. Which is in
  // fact what we want - but by accident rather than by decision, and the
  // ref says so out loud.
  const modelRef = useRef<string | null>(model);
  /** Watchdog for a reply whose playback never reports finishing. */
  const speechTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopListening = () => {
    recorderRef.current?.stop();
    recognizerRef.current?.stop();
    setListening(false);
    setLevel(0);
  };

  // Always stop the mic AND the voice when the surface unmounts — a partner
  // still talking after the learner has navigated away is alarming.
  useEffect(
    () => () => {
      liveRef.current = false;
      recorderRef.current?.stop();
      recognizerRef.current?.stop();
      cancelVoice();
      if (speechTimer.current) clearTimeout(speechTimer.current);
    },
    [],
  );

  const buildRecognizer = () => {
    if (!sttAvailable()) return;
    recognizerRef.current = createRecognizer({
      lang,
      onFinal: (text) => {
        void handleUtterance(text);
      },
      // Shows the learner they are being heard, and — more importantly —
      // means a pause mid-sentence is visibly a pause, not a dead mic.
      onInterim: (text) => setHeard(text),
      // Only fires when WE stopped it; the recogniser's own stops are
      // restarted inside createRecognizer, so a learner thinking for five
      // seconds does not lose the microphone.
      onEnd: () => setListening(false),
    });
  };

  /** Decide who listens for this conversation, and build it.
   *
   * Server listening needs the browser to be able to record AND the
   * server to be able to transcribe; either missing means the browser's
   * own recogniser, exactly as before. Asked once per conversation, not
   * per turn — the answer does not change mid-chat. */
  const buildListener = async () => {
    let server = false;
    let maxClipMs = 60_000;
    if (recorderAvailable()) {
      try {
        const status = await voiceStatus();
        server = !!status.transcription;
        if (status.max_clip_seconds) maxClipMs = status.max_clip_seconds * 1000;
      } catch {
        server = false;
      }
    }
    serverRef.current = server;
    setCanFinish(server);
    if (!server) {
      buildRecognizer();
      return;
    }
    recorderRef.current = createRecorder({
      ...silenceWindowFor(cefrLevel),
      maxMs: maxClipMs,
      onClip: (clip, seconds, mime) => {
        void handleClip(clip, seconds, mime);
      },
      onLevel: setLevel,
      onError: (e) => {
        // A refused microphone is the realistic case. Say so and stop
        // pretending to listen; the typed box still works.
        setError(e instanceof Error && e.message ? e.message : "microphone unavailable");
        setListening(false);
        setLevel(0);
      },
    });
  };

  const startListening = () => {
    if (!liveRef.current) return;
    // NOT clearing `missed` here: re-arming the microphone is exactly what
    // happens right after a miss, and clearing it then meant the "say it
    // again" message vanished before anyone could read it. It clears when
    // the next turn actually arrives.
    if (serverRef.current && recorderRef.current) {
      setListening(true);
      void recorderRef.current.start();
      return;
    }
    if (!recognizerRef.current) return;
    setListening(true);
    recognizerRef.current.start();
  };

  /** Longest we will wait for a spoken reply to report that it finished.
   *  German runs about 15 characters a second; this is generous on top. */
  const speechCeiling = (text: string) => Math.min(60_000, 4000 + text.length * 120);

  const speakThenListen = (text: string) => {
    // The server's voice where the deployment has one, the browser's
    // otherwise. speakBest is supposed to guarantee onEnd either way, and
    // onEnd is what re-arms the microphone — so if it is ever missed the
    // conversation simply stops, with the partner apparently mid-sentence.
    // iOS drops speechSynthesis utterances silently often enough that the
    // guarantee cannot be the only thing holding the loop together.
    if (speechTimer.current) clearTimeout(speechTimer.current);
    let handed = false;
    const hand = () => {
      if (handed) return;
      handed = true;
      if (speechTimer.current) clearTimeout(speechTimer.current);
      speechTimer.current = null;
      setSpeaking(false);
      startListening();
    };
    setSpeaking(true);
    speechTimer.current = setTimeout(hand, speechCeiling(text));
    void speakBest(text, { lang, onEnd: hand });
  };

  const finish = (row: api.SpeakingSession) => {
    liveRef.current = false;
    stopListening();
    cancelVoice();
    if (speechTimer.current) clearTimeout(speechTimer.current);
    speechTimer.current = null;
    setSpeaking(false);
    setTranscribing(false);
    setPaused(false);
    setEnded(row);
    onFinished?.();
  };

  const handleUtterance = async (text: string) => {
    const sessionId = sessionRef.current;
    if (!sessionId || busyRef.current) return;
    stopListening();
    setMissed(false);
    setMessages((m) => [...m, { role: "learner", text }]);
    setHeard("");
    setPaused(false);
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const r = await api.replySpeaking(sessionId, text, modelRef.current);
      // The verdict on what they just said is attached to THEIR line, so
      // it sits under their own words rather than inside the partner's.
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "learner") {
          copy[copy.length - 1] = {
            ...last,
            ok: r.ok ?? undefined,
            correction: r.correction ?? null,
          };
        }
        return [...copy, { role: "assistant", text: r.reply }];
      });
      setTurns(r.turns);
      if (r.ended) finish(r);
      else speakThenListen(r.reply);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  /** A recorded turn: write it down on the server, then treat the text
   *  exactly like a typed or recognised one. */
  const handleClip = async (clip: Blob, seconds: number, mime: string) => {
    if (!sessionRef.current || busyRef.current || clipRef.current) return;
    clipRef.current = true;
    // The partner must not hear itself: nothing is recorded from here
    // until its reply has been spoken.
    recorderRef.current?.stop();
    setListening(false);
    setLevel(0);
    setMissed(false);
    setTranscribing(true);
    setError(null);
    try {
      const out = await transcribe(clip, {
        filename: filenameFor(mime),
        language: lang ?? "de-DE",
        seconds,
      });
      setTranscribing(false);
      if (!out.text.trim()) {
        // Heard nothing usable. Not an error — ask, and listen again.
        setMissed(true);
        startListening();
        return;
      }
      await handleUtterance(out.text.trim());
    } catch (e) {
      setTranscribing(false);
      setError(e instanceof ApiError ? e.message : "error");
      // Whatever went wrong, the microphone comes back so they can retry.
      startListening();
    } finally {
      clipRef.current = false;
    }
  };

  const start = async () => {
    // MUST happen inside the click that called this. iOS only lets a page
    // play audio through an element it has already played during a real
    // gesture — without this the partner speaks its opening line and is
    // silent for the rest of the conversation.
    unlockVoice();
    setBusy(true);
    setError(null);
    try {
      const r = await startSession();
      sessionRef.current = r.id;
      liveRef.current = true;
      setSession({ id: r.id, maxTurns: r.max_turns });
      setMessages([{ role: "assistant", text: r.reply }]);
      setTurns(0);
      setEnded(null);
      setPaused(false);
      setMissed(false);
      await buildListener();
      speakThenListen(r.reply);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    const sessionId = sessionRef.current;
    if (!sessionId) return;
    stopListening();
    setBusy(true);
    setError(null);
    try {
      finish(await api.endSpeaking(sessionId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      setBusy(false);
    }
  };

  const sendText = (text: string) => {
    const cleaned = text.trim();
    if (cleaned) void handleUtterance(cleaned);
  };

  const finishTurn = () => {
    recorderRef.current?.finish();
  };

  /** Put the learner back into a conversation the server still has open.
   *
   * Deliberately does NOT arm the microphone or re-speak anything: a page
   * that started talking on its own after a refresh would be alarming, and
   * browsers want a user gesture before re-opening the mic anyway. The
   * transcript comes back and a Continue button does the rest. */
  const resume = (row: api.SpeakingSession) => {
    sessionRef.current = row.id;
    liveRef.current = true;
    setSession({ id: row.id, maxTurns: row.max_turns });
    setMessages(row.transcript);
    setTurns(row.turns);
    setEnded(null);
    setError(null);
    setPaused(true);
    void buildListener();
  };

  /** Arm the mic on a resumed conversation (the Continue button). Also a
   *  user gesture, so unlock the audio here too. */
  const listen = () => {
    unlockVoice();
    setPaused(false);
    startListening();
  };

  const reset = () => {
    sessionRef.current = null;
    liveRef.current = false;
    stopListening();
    setSession(null);
    setMessages([]);
    setTurns(0);
    setEnded(null);
    setError(null);
    setPaused(false);
    setHeard("");
    setSpeaking(false);
    setTranscribing(false);
    setMissed(false);
    if (speechTimer.current) clearTimeout(speechTimer.current);
    speechTimer.current = null;
  };

  return {
    session, messages, turns, busy, listening, speaking, transcribing, missed,
    level, canFinish, error, ended, paused, heard,
    start, sendText, finishTurn, end, reset, resume, listen, showEnded: setEnded,
  };
}
